import { Alert, Heading, IconButton, Pane, RefreshIcon, Spinner } from 'evergreen-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Api from '../../../util/Api';
import DataTable from '../../common/DataTable/DataTable';
import { getOrderColumns } from '../../orders/components/orderColumns';
import { TicketsModal, useTicketsModal } from '../../../v2/features/tickets/components/EntityTickets';

const LIMIT = 10;

function OrdersToProcess() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: LIMIT, totalCount: 0, totalPages: 0 });
  const pageRef = useRef(1);
  const tickets = useTicketsModal();

  const fetchOrders = useCallback(async (page = 1) => {
    pageRef.current = page;
    try {
      setLoading(true);
      setError(null);
      const response = await Api.get(`/dashboard/orders-to-process?page=${page}&limit=${LIMIT}`);
      if (response.success) {
        setOrders(response.orders);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch orders');
      }
    } catch (e) {
      console.error('Error fetching orders to process:', e);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(1);
    const id = setInterval(() => fetchOrders(pageRef.current), 60000);
    return () => clearInterval(id);
  }, [fetchOrders]);

  const columns = useMemo(() => {
    const c = getOrderColumns({ onOpenThreads: tickets.open, withPreview: true });
    return [c.issue, c.orderNumber, c.tags, c.customer, c.status, c.payment, c.delivery, c.grandTotal, c.createdAt];
  }, [tickets.open]);

  const handleTableChange = (opts) => {
    const nextPage = Math.floor((opts.start || 0) / LIMIT) + 1;
    // Skip the no-op page-1 sync DataTable emits right after the initial fetch.
    if (nextPage === pageRef.current) return;
    fetchOrders(nextPage);
  };

  return (
    <Pane>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={600}>
          Orders to Process ({pagination.totalCount})
        </Heading>
        <IconButton icon={RefreshIcon} onClick={() => fetchOrders(pageRef.current)} disabled={loading} />
      </Pane>

      {loading && orders.length === 0 && (
        <Pane display="flex" alignItems="center" justifyContent="center" padding={40}>
          <Spinner />
        </Pane>
      )}

      {error && !loading && (
        <Pane padding={16}>
          <Alert intent="danger" title="Error loading orders">
            {error}
          </Alert>
        </Pane>
      )}

      {!loading && !error && orders.length === 0 && (
        <Pane padding={16}>
          <Alert intent="success" title="All caught up!">
            There are no orders waiting to be processed.
          </Alert>
        </Pane>
      )}

      {orders.length > 0 && (
        <DataTable
          columns={columns}
          data={orders}
          updateKey={orders}
          page={pagination.page}
          pageSize={LIMIT}
          pageCount={pagination.totalPages}
          onTableChange={handleTableChange}
          paginationMode="paged"
          search={false}
          filters={false}
          height="auto"
        />
      )}

      <TicketsModal target={tickets.target} onClose={tickets.close} />
    </Pane>
  );
}

export default OrdersToProcess;
